import { Navigate, createBrowserRouter } from 'react-router'

import RouteErrorBoundary from 'src/core/containers/RouteErrorBoundary'
import { lazyWithRetry } from 'src/core/utils/lazyWithRetry'

import FFProtectedRoute from './components/FFProtectedRoute'
import RedirectWithSearch from './components/RedirectWithSearch'
import RoleProtectedRoute from './components/RoleProtectedRoute'
import ROUTES from './routes'
import { CustomerRouteObject } from './types'

const AppWrapper = lazyWithRetry(() => import('src/routes/containers/AppWrapper'))
const AuthenticatedAppWrapper = lazyWithRetry(() => import('src/routes/containers/AuthenticatedAppWrapper'))
const PublicLayout = lazyWithRetry(() => import('src/core/containers/PublicLayout'))
const ExternalFeedback = lazyWithRetry(() => import('src/pages/ExternalFeedback'))
const Dashboard = lazyWithRetry(() => import('src/core/containers/Dashboard'))
const MaintenanceError = lazyWithRetry(() => import('src/pages/NotFound'))
const PatientContact = lazyWithRetry(() => import('src/pages/PatientContact'))
const PatientContacts = lazyWithRetry(() => import('src/pages/PatientContacts'))
const Dictation = lazyWithRetry(() => import('src/pages/Dictation'))
const Insights = lazyWithRetry(() => import('src/pages/Insights'))
const Settings = lazyWithRetry(() => import('src/pages/Settings'))
const PaidFeature = lazyWithRetry(() => import('src/pages/PaidFeature'))
const AuthWrapper = lazyWithRetry(() => import('src/core/containers/AuthWrapper'))
const Login = lazyWithRetry(() => import('src/pages/Login'))
const LoginSuccess = lazyWithRetry(() => import('src/pages/LoginSuccess'))
// const Register = lazyWithRetry(() => import('src/pages/Register'))
const ForgotPassword = lazyWithRetry(() => import('src/pages/ForgotPassword'))
const VerifyEmail = lazyWithRetry(() => import('src/pages/VerifyEmail'))
const ResetPassword = lazyWithRetry(() => import('src/pages/password/ResetPassword'))
const SetPassword = lazyWithRetry(() => import('src/pages/password/SetPassword'))
const VerifyMFA = lazyWithRetry(() => import('src/pages/VerifyMFA'))
const AuthenticationRedirect = lazyWithRetry(() => import('src/pages/AuthenticationRedirect'))

const routes: CustomerRouteObject[] = [
	{
		path: '/',
		element: <Navigate to={'/login'} replace />,
	},

	{
		path: '/',
		element: <AppWrapper />,
		errorElement: <RouteErrorBoundary />,
		children: [
			{
				path: '/',
				element: <AuthenticatedAppWrapper />,
				children: [
					{
						path: '/',
						element: <AuthWrapper />,
						children: [
							{
								path: ROUTES.LOGIN,
								element: <Login />,
							},
							{
								path: ROUTES.LOGIN_SUCCESS,
								element: <LoginSuccess />,
							},
							// {
							// 	path: ROUTES.REGISTER,
							// 	element: <Register />,
							// },
							{
								path: ROUTES.FORGOT_PASSWORD,
								element: <ForgotPassword />,
							},
							{
								path: ROUTES.VERIFY_EMAIL,
								element: <VerifyEmail />,
							},
							{
								path: ROUTES.RESET_PASSWORD,
								element: <ResetPassword />,
							},
							{
								path: ROUTES.SET_PASSWORD,
								element: <SetPassword />,
							},
							{
								path: ROUTES.VERIFY_MFA,
								element: <VerifyMFA />,
							},
							{
								path: ROUTES.AUTHENTICATION_REDIRECT,
								element: <AuthenticationRedirect />,
							},
						],
					},
					/**
					 * Make sure the old integration links still work
					 * e.g. /create-records/patient-contact?name=Jan%20Janssens&note=&gender=male&context=tekst&contact_id=2&patient_id=64558595&token=some_token
					 */
					{
						path: '/create-records/patient-contact',
						element: <RedirectWithSearch to={ROUTES.PATIENT_CONTACTS} />,
					},
					{
						path: '/',
						element: <Dashboard />,
						children: [
							{
								path: ROUTES.PATIENT_CONTACTS,
								element: <PatientContacts />,
							},
							{
								path: ROUTES.PATIENT_CONTACT,
								element: <FFProtectedRoute featureFlag="consultations" element={<PatientContact />} />,
							},
							{
								path: ROUTES.DICTATIONS,
								element: <Navigate to={ROUTES.PATIENT_CONTACTS} />,
							},
							{
								path: ROUTES.DICTATION,
								element: <FFProtectedRoute featureFlag="consultations" element={<Dictation />} />,
							},
							{
								path: ROUTES.INSIGHTS,
								element: (
									<RoleProtectedRoute
										allowedRoles={['orgAdmin', 'superAdmin']}
										element={<Insights />}
									/>
								),
							},
							{
								path: ROUTES.INSIGHTS_TAB,
								element: (
									<RoleProtectedRoute
										allowedRoles={['orgAdmin', 'superAdmin']}
										element={<Insights />}
									/>
								),
							},
							{
								path: ROUTES.SETTINGS,
								element: <Settings />,
							},
							{
								path: ROUTES.SETTINGS_TAB,
								element: <Settings />,
							},
							{
								path: ROUTES.PAID_FEATURE,
								element: <PaidFeature />,
							},
						],
					},
					{
						path: ROUTES.NOT_FOUND,
						element: <MaintenanceError />,
					},
				],
			},
			{
				path: '/',
				element: <PublicLayout />,
				children: [
					{
						path: ROUTES.EXTERNAL_FEEDBACK,
						element: <ExternalFeedback />,
					},
				],
			},
		],
	},
	{
		path: '*',
		element: <Navigate to={ROUTES.NOT_FOUND} replace />,
	},
]

const router = createBrowserRouter(routes, { basename: import.meta.env.VITE_BASE_NAME })

export default router
